"use client"

import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import {
  Typography,
  Box,
  alpha,
  Tooltip,
  IconButton,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Autocomplete,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  useMediaQuery,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import SearchIcon from "@mui/icons-material/Search"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import ReplayIcon from "@mui/icons-material/Replay"
import MenuIcon from "@mui/icons-material/Menu"
import CloseIcon from "@mui/icons-material/Close"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import PostAddIcon from "@mui/icons-material/PostAdd"
import SendIcon from "@mui/icons-material/Send"
import FilterListIcon from "@mui/icons-material/FilterList"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PersonIcon from "@mui/icons-material/Person"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { DataGrid } from "@mui/x-data-grid"
import dayjs from "dayjs"
import Swal from "sweetalert2"

import { ErpContext } from "../../context/ErpContext.js"
import { formatPersonaApellidoNombre } from "../../utils/personaDisplay.js"
import "./TablaVentasComponent.css"
import SpinnerLading from "../misc/SpinnerComponent.js"
import CustomPagination from "./CustomPagination.js"
import ModalNuevaRetencionArca from "./ModalNuevaRetencionArca.js"

/** Coincide con defaultDataValidator: tipo «Retención ARCA». */
const ID_TIPO_TRANSACCION_RETENCION_ARCA = 12

function formatDateOnly(val) {
  if (val == null || val === "") return ""
  const d = new Date(val)
  if (Number.isNaN(d.getTime())) return String(val)
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d)
}

function alicuotaDerivada(ttf) {
  const b = Number(ttf?.baseImponibleSire)
  const i = Number(ttf?.importeRetenidoSire)
  if (!Number.isFinite(b) || b <= 0 || !Number.isFinite(i)) return null
  return Math.round((i / b) * 10000) / 100
}

function estadoRetencionDesdeTtf(ttf) {
  const n = ttf?.nroCertificadoSire
  const tieneCert = n != null && String(n).trim() !== ""
  return tieneCert ? "informada" : "pendiente"
}

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#5b5b5f" },
    secondary: { main: "rgb(244, 177, 14)" },
    background: { default: "#f8f9fa", paper: "#ffffff" },
    text: { primary: "#2d3748", secondary: "#718096" },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8, textTransform: "none", boxShadow: "none", fontWeight: 500 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" },
      },
    },
  },
})

export default function TablaVentasRetenciones() {
  const {
    getUbicacion,
    ubicaciones,
    getTransaccionTransformedVentas,
    getFechaHoraActualClient,
    isNavbarCollapsed,
    getEntidadesClientesTresLetras,
    entidadesClientesTresLetras,
    postInformarRetencionSire,
    postDryRunSire,
    postAuthorizeSire,
    postDummySire,
  } = useContext(ErpContext)

  const isLaptopDown = useMediaQuery("(max-width:1200px)")
  const isMobile = useMediaQuery("(max-width:600px)")

  const [openModal, setOpenModal] = useState(false)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const today = dayjs()
  const [startDate, setStartDate] = useState(today.subtract(30, "day"))
  const [endDate, setEndDate] = useState(today)
  const [page, setPage] = useState(1)
  const [limit] = useState(100)
  const DEFAULT_SORT = [{ field: "fechaHoraCreacionTimestamp", sort: "desc" }]
  const [sortModel, setSortModel] = useState(DEFAULT_SORT)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)
  const [minVentas, setMinVentas] = useState(0)
  const [maxVentas, setMaxVentas] = useState(0)
  const [mostrarDeletes, setMostrarDeletes] = useState(false)
  const [idTransaccionSearch, setIdTransaccionSearch] = useState(null)
  const [filtersActive, setFiltersActive] = useState(false)
  const [mostrarMasFiltros, setMostrarMasFiltros] = useState(false)
  const [informingId, setInformingId] = useState(null)
  const [filtersOpen, setFiltersOpen] = useState(!isLaptopDown)

  // Filtros específicos retenciones
  const [dniCliente, setDniCliente] = useState(null)
  const [searchTextCliente, setSearchTextCliente] = useState("")
  const [soloPendientes, setSoloPendientes] = useState(false)
  const [buscarCuitProveedor, setBuscarCuitProveedor] = useState("")
  const [filtroEstado, setFiltroEstado] = useState("todos") // "todos" | "pendiente" | "informada"

  useEffect(() => {
    setFiltersOpen(!isLaptopDown)
  }, [isLaptopDown])

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, null)
    }
  }, [searchTextCliente])

  const fetchDataInicio = async () => {
    try {
      await getUbicacion()
    } catch (e) {
      console.error(e)
    }
  }

  const fetchData = async (
    date,
    pageArg,
    endDateArg,
    selectedUbicacionArg,
    valorMin,
    valorMax,
    mostrarDeletesArg,
    idTransaccionSearchArg,
    dniClienteArg = null,
    sortFieldParam = null,
    sortOrderParam = null,
  ) => {
    const controller = new AbortController()
    const signal = controller.signal
    try {
      setLoading(true)

      let formattedDate = date ? date.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")
      let formattedDate2 = endDateArg
      let idSelectedUbicacion = null
      let idDni = null

      if (endDateArg && typeof endDateArg.format === "function") {
        formattedDate2 = endDateArg.format("YYYY-MM-DD")
      }
      if (selectedUbicacionArg != null) {
        idSelectedUbicacion = selectedUbicacionArg.id
      }
      if (dniClienteArg != null) {
        idDni = dniClienteArg.id
      }

      const result1 = await getFechaHoraActualClient(formattedDate)
      formattedDate = dayjs(result1.fechaServidor).startOf("day").toDate()

      const result2 = await getFechaHoraActualClient(formattedDate2)
      formattedDate2 = dayjs(result2.fechaServidor).endOf("day").toDate()

      const activeSort = Array.isArray(sortModel) && sortModel.length ? sortModel[0] : DEFAULT_SORT[0]
      const sortField = sortFieldParam || activeSort?.field || "fechaHoraCreacionTimestamp"
      const sortOrder = sortOrderParam || (activeSort?.sort === "asc" ? "asc" : "desc")

      const data = await getTransaccionTransformedVentas(
        String(ID_TIPO_TRANSACCION_RETENCION_ARCA),
        formattedDate,
        pageArg,
        limit,
        { signal },
        formattedDate2,
        idDni,
        idSelectedUbicacion,
        valorMin,
        valorMax,
        mostrarDeletesArg,
        idTransaccionSearchArg,
        "",
        "",
        null,
        null,
        false,
        null,
        null,
        sortField,
        sortOrder,
      )

      if (!data?.data) {
        setRows([])
        setTotalPages(0)
        setTotalCount(0)
        setMontoTotalTransacciones(0)
        return
      }

      const mapped = data.data.map((transaccion) => {
        const ttf = transaccion.transaccionTipoFactura
        const tp = transaccion.transaccionPago || []
        const pagosTransformados = tp.map((tpi) => {
          const pago = tpi.pago || {}
          return {
            montoTotal: pago.montoTotal || 0,
            cotizacion: pago.cotizacion || 1,
            medioDePago: pago.medioDePago?.descripcion || "",
            id: pago.medioDePago?.id || "",
            tipoMedio: pago.medioDePago?.tipoMedioDePago?.descripcion || "",
          }
        })

        const fechaBase = transaccion.fechaHoraCreacion ? new Date(transaccion.fechaHoraCreacion) : null
        const fechaTimestamp = fechaBase && !Number.isNaN(fechaBase.getTime()) ? fechaBase.getTime() : null
        const fechaDisplay = fechaBase
          ? new Intl.DateTimeFormat("es-AR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }).format(fechaBase)
          : ""

        const cliente = formatPersonaApellidoNombre({
          descripcion: transaccion.entidad?.descripcion,
          apellido: transaccion.entidad?.apellido,
        })

        return {
          id: transaccion.id,
          fechaHoraCreacionTimestamp: fechaTimestamp,
          fechaHoraCreacionDisplay: fechaDisplay,
          descripcion: transaccion.descripcion || "",
          montoTotal: transaccion.montoTotal,
          idTipoTransaccionID: transaccion.idTipoTransaccion,
          tipoTransaccion: transaccion.tipoTransaccion,
          transaccionTipoFactura: ttf,
          pagos: pagosTransformados,
          descripcionEntidad: cliente || transaccion.entidad?.descripcion || "",
          dniCuitCuil: transaccion.entidad?.dniCuitCuil || "",
          usuario: transaccion.usuario?.usuario || "",
          ubicacion: transaccion.ubicacion?.descripcion || "",
          fechaHoraCreacion: transaccion.fechaHoraCreacion,
          cuitProveedor: ttf?.cuitProveedorSire || "",
          regimen: ttf?.regimenSire ?? "",
          baseImponible: ttf?.baseImponibleSire != null ? Number(ttf.baseImponibleSire) : null,
          alicuota: alicuotaDerivada(ttf),
          importeRetenido: ttf?.importeRetenidoSire != null ? Number(ttf.importeRetenidoSire) : null,
          fechaFactura: formatDateOnly(ttf?.fechaFacturaSire),
          fechaRetencion: formatDateOnly(ttf?.fechaRetencionSire),
          estadoInterno: estadoRetencionDesdeTtf(ttf),
          nroCertificado: ttf?.nroCertificadoSire || "",
          codigoSeguridad: ttf?.codigoSeguridadSire || "",
        }
      })

      setPage(data.currentPage)
      setTotalPages(data.totalPages)
      setTotalCount(data.total)
      setMontoTotalTransacciones(data.totalMontoVentas ?? 0)
      setRows(mapped)
    } catch (e) {
      if (e?.name === "AbortError" || e?.message === "canceled") return
      console.error(e)
      Swal.fire({ icon: "error", title: "Error", text: e.message || "No se pudieron cargar las retenciones." })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDataInicio()
  }, [])

  useEffect(() => {
    fetchData(startDate, page, endDate, selectedUbicacion, minVentas, maxVentas, mostrarDeletes, idTransaccionSearch, dniCliente)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate])

  useEffect(() => {
    fetchData(startDate, page, endDate, selectedUbicacion, minVentas, maxVentas, mostrarDeletes, idTransaccionSearch, dniCliente)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  useEffect(() => {
    const hasActiveFilters =
      (endDate && startDate && !endDate.isSame(startDate, "day")) ||
      selectedUbicacion ||
      minVentas > 0 ||
      maxVentas > 0 ||
      idTransaccionSearch ||
      mostrarDeletes ||
      dniCliente ||
      filtroEstado !== "todos" ||
      buscarCuitProveedor.trim()

    setFiltersActive(!!hasActiveFilters)
  }, [startDate, endDate, selectedUbicacion, minVentas, maxVentas, idTransaccionSearch, mostrarDeletes, dniCliente, filtroEstado, buscarCuitProveedor])

  const buttonFiltrado = () => {
    setPage(1)
    fetchData(startDate, 1, endDate, selectedUbicacion, minVentas, maxVentas, mostrarDeletes, idTransaccionSearch, dniCliente)
  }

  const handleSortModelChange = (newModel) => {
    const nextSort = Array.isArray(newModel) && newModel.length ? newModel[0]?.sort : "desc"
    const normalizedSort = nextSort === "asc" ? "asc" : "desc"
    const nextModel = [{ field: "fechaHoraCreacionTimestamp", sort: normalizedSort }]
    setSortModel(nextModel)
    setPage(1)
    fetchData(
      startDate,
      1,
      endDate,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch,
      dniCliente,
      nextModel[0].field,
      normalizedSort,
    )
  }

  const resetFilters = () => {
    setSelectedUbicacion(null)
    setMinVentas(0)
    setMaxVentas(0)
    setMostrarDeletes(false)
    setIdTransaccionSearch(null)
    setMostrarMasFiltros(false)
    setDniCliente(null)
    setSearchTextCliente("")
    setSoloPendientes(false)
    setBuscarCuitProveedor("")
    setFiltroEstado("todos")
    const t = dayjs()
    setStartDate(t.subtract(30, "day"))
    setEndDate(t)
    setPage(1)
    fetchData(t.subtract(30, "day"), 1, t, null, 0, 0, false, null, null)
  }

  const increaseDate = () => setStartDate((prev) => (prev ? prev.add(1, "day") : dayjs()))
  const decreaseDate = () => setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()))

  const ubicacionesFiltradas = useMemo(
    () => (ubicaciones || []).filter((u) => u.esDestino === false),
    [ubicaciones],
  )

  // Filtros client-side: estado y CUIT proveedor
  const filteredRows = useMemo(() => {
    let result = rows
    if (filtroEstado !== "todos") {
      result = result.filter((r) => r.estadoInterno === filtroEstado)
    }
    const cuitTrim = buscarCuitProveedor.trim()
    if (cuitTrim) {
      result = result.filter((r) => String(r.cuitProveedor).includes(cuitTrim))
    }
    return result
  }, [rows, filtroEstado, buscarCuitProveedor])

  const informarSire = useCallback(
    async (id) => {
      setInformingId(id)
      try {
        const { data } = await postInformarRetencionSire(id)
        if (!data.ok) throw new Error(data.message)
        Swal.fire({ icon: "success", title: "Informado en SIRE", timer: 2000, showConfirmButton: false })
        await fetchData(startDate, page, endDate, selectedUbicacion, minVentas, maxVentas, mostrarDeletes, idTransaccionSearch, dniCliente)
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: "SIRE",
          text: e.response?.data?.message || e.message,
        })
      } finally {
        setInformingId(null)
      }
    },
    [startDate, page, endDate, selectedUbicacion, minVentas, maxVentas, mostrarDeletes, idTransaccionSearch, dniCliente],
  )

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 72,
        headerAlign: "center",
        renderCell: (p) => (
          <Typography variant="body2" fontWeight={600}>
            {p.value}
          </Typography>
        ),
      },
      {
        field: "fechaHoraCreacionTimestamp",
        headerName: "Fecha / hora",
        flex: 1,
        minWidth: 168,
        sortable: true,
        headerAlign: "center",
        renderCell: (p) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%" }}>
            <CalendarTodayIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{p.row.fechaHoraCreacionDisplay || ""}</Typography>
          </Box>
        ),
      },
      {
        field: "cuitProveedor",
        headerName: "CUIT proveedor",
        minWidth: 136,
        headerAlign: "center",
        renderCell: (p) => (
          <Tooltip title={p.value || ""}>
            <Typography variant="body2" noWrap sx={{ fontFamily: "monospace" }}>
              {p.value || "—"}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: "descripcionEntidad",
        headerName: "Cliente",
        flex: 0.9,
        minWidth: 140,
        headerAlign: "center",
        renderCell: (p) => (
          <Tooltip title={p.value || ""}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
              <Typography variant="body2" noWrap fontWeight={500}>
                {p.value || "—"}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "ubicacion",
        headerName: "Ubicación",
        minWidth: 120,
        headerAlign: "center",
        renderCell: (p) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.6), fontSize: 18 }} />
            <Typography variant="body2" noWrap>
              {p.value || "—"}
            </Typography>
          </Box>
        ),
      },
      { field: "regimen", headerName: "Rég.", width: 68, headerAlign: "center" },
      {
        field: "baseImponible",
        headerName: "Base imp.",
        width: 108,
        headerAlign: "center",
        renderCell: (p) =>
          p.value != null ? (
            <Chip
              size="small"
              icon={<AttachMoneyIcon fontSize="small" />}
              label={Number(p.value).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
              sx={{ bgcolor: alpha("#9e9e9e", 0.1), color: "#555", "& .MuiChip-icon": { color: "#777" } }}
            />
          ) : (
            "—"
          ),
      },
      {
        field: "alicuota",
        headerName: "Alíc.%",
        width: 76,
        headerAlign: "center",
        renderCell: (p) => (p.value != null ? `${Number(p.value).toFixed(2)}%` : "—"),
      },
      {
        field: "importeRetenido",
        headerName: "Retenido",
        width: 112,
        headerAlign: "center",
        renderCell: (p) =>
          p.value != null ? (
            <Chip
              size="small"
              icon={<AttachMoneyIcon fontSize="small" />}
              label={Number(p.value).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
              sx={{ bgcolor: alpha("#7b68ee", 0.1), color: "#4a3fa0", "& .MuiChip-icon": { color: "#7b68ee" } }}
            />
          ) : (
            "—"
          ),
      },
      { field: "fechaFactura", headerName: "F. factura", width: 104, headerAlign: "center" },
      { field: "fechaRetencion", headerName: "F. retenc.", width: 104, headerAlign: "center" },
      {
        field: "estadoInterno",
        headerName: "Estado",
        width: 120,
        headerAlign: "center",
        renderCell: (p) => {
          const ok = p.value === "informada"
          return (
            <Chip
              size="small"
              label={ok ? "Informada" : "Pendiente"}
              sx={{
                fontWeight: 600,
                bgcolor: ok ? alpha("#2e7d32", 0.12) : alpha("#ed6c02", 0.12),
                color: ok ? "#1b5e20" : "#e65100",
              }}
            />
          )
        },
      },
      {
        field: "nroCertificado",
        headerName: "Cert. nº",
        flex: 0.5,
        minWidth: 120,
        headerAlign: "center",
        renderCell: (p) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
            {p.value || "—"}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "Acciones",
        width: 148,
        sortable: false,
        headerAlign: "center",
        renderCell: (params) => {
          const pendiente = params.row.estadoInterno === "pendiente"
          if (!pendiente) return null
          return (
            <Button
              size="small"
              variant="outlined"
              startIcon={informingId === params.row.id ? <CircularProgress size={14} /> : <SendIcon />}
              disabled={informingId === params.row.id}
              onClick={() => informarSire(params.row.id)}
              sx={{ textTransform: "none", borderColor: alpha("#7b68ee", 0.6), color: "#4a3fa0" }}
            >
              Informar SIRE
            </Button>
          )
        },
      },
    ],
    [informingId, informarSire],
  )

  if (loading && rows.length === 0) {
    return <SpinnerLading />
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#f8f9fa",
      }}
    >
      <ThemeProvider theme={theme}>
        {/* Barra móvil superior */}
        {isLaptopDown && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1.5,
              backgroundColor: "white",
              borderBottom: "1px solid #e0e0e0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(!filtersOpen)}
              sx={{ textTransform: "none", borderRadius: 2, px: 2 }}
            >
              {filtersOpen ? "Ocultar filtros" : "Mostrar filtros"}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={buttonFiltrado}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: "#5b5b5f",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
                "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
              }}
            >
              Buscar
            </Button>
          </Box>
        )}

        <Box sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
          {/* Panel principal de filtros */}
          <Collapse in={filtersOpen || !isLaptopDown} timeout={300}>
            <Paper
              elevation={1}
              sx={{
                m: 1.5,
                mb: 1,
                borderRadius: 3,
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: "1px solid #e0e0e0",
              }}
            >
              <Box sx={{ p: 1.25, width: "100%" }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center", width: "100%" }}>
                  {/* Navegación de fechas */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <IconButton
                        onClick={decreaseDate}
                        color="primary"
                        size="small"
                        sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                      >
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <DatePicker
                        value={startDate}
                        onChange={(v) => setStartDate(v)}
                        format="DD/MM/YYYY"
                        label="Desde"
                        slotProps={{ textField: { size: "small", sx: { width: 132 } } }}
                      />
                      <DatePicker
                        value={endDate}
                        onChange={(v) => setEndDate(v)}
                        format="DD/MM/YYYY"
                        label="Hasta"
                        slotProps={{ textField: { size: "small", sx: { width: 132 } } }}
                      />
                      <IconButton
                        onClick={increaseDate}
                        color="primary"
                        size="small"
                        sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                      >
                        <ArrowForwardIcon fontSize="small" />
                      </IconButton>
                    </LocalizationProvider>
                  </Box>

                  {/* Filtros de texto y selects */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, flex: "1 1 0", minWidth: 0 }}>
                    <Autocomplete
                      size="small"
                      options={ubicacionesFiltradas}
                      getOptionLabel={(o) => o?.descripcion || ""}
                      value={selectedUbicacion}
                      onChange={(_, v) => setSelectedUbicacion(v)}
                      sx={{ minWidth: 150, flex: "1 1 130px" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Ubicación"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.6) }} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />

                    {/* Búsqueda por cliente (entity) */}
                    <Autocomplete
                      size="small"
                      options={entidadesClientesTresLetras || []}
                      getOptionLabel={(o) => `${o?.descripcion || ""} ${o?.apellido || ""}`.trim() + (o?.dniCuitCuil ? ` — ${o.dniCuitCuil}` : "")}
                      value={dniCliente}
                      onChange={(_, v) => setDniCliente(v)}
                      inputValue={searchTextCliente}
                      onInputChange={(_, v) => setSearchTextCliente(v)}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      sx={{ minWidth: 180, flex: "1 1 160px" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Buscar cliente"
                          placeholder="3+ letras..."
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.6) }} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />

                    <Tooltip title="Nueva retención ARCA (SIRE)">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PostAddIcon />}
                        onClick={() => setOpenModal(true)}
                        sx={{ textTransform: "none", fontWeight: 500, borderRadius: 2, whiteSpace: "nowrap" }}
                      >
                        Nueva retención
                      </Button>
                    </Tooltip>

                    {/* Autorizar SIRE en ARCA (experimental) */}
                    <Tooltip title="Intentar autorizar sire-ws vía automatización AFIP SDK">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          try {
                            Swal.fire({ title: "Autorizando SIRE...", text: "Intentando autorizar sire-ws vía automatización AFIP SDK", allowOutsideClick: false, didOpen: () => Swal.showLoading() })
                            const resp = await postAuthorizeSire()
                            Swal.fire({ icon: "success", title: "Autorización OK", html: `<p>${resp.data?.message || "Ejecutado correctamente"}</p><pre style="text-align:left;font-size:12px;max-height:200px;overflow:auto">${JSON.stringify(resp.data?.automationResponse, null, 2)}</pre>`, confirmButtonText: "Cerrar" })
                          } catch (err) {
                            const data = err.response?.data || {}
                            const instrucciones = data.instruccionesManual?.join("\n") || ""
                            Swal.fire({ icon: "error", title: "No se pudo autorizar", html: `<p>${data.message || err.message}</p>${instrucciones ? `<pre style="text-align:left;font-size:11px;white-space:pre-wrap">${instrucciones}</pre>` : ""}`, confirmButtonText: "Entendido" })
                          }
                        }}
                        sx={{ textTransform: "none", fontWeight: 500, borderRadius: 2, whiteSpace: "nowrap", borderColor: alpha("#e67e22", 0.6), color: "#e67e22" }}
                      >
                        Autorizar SIRE
                      </Button>
                    </Tooltip>

                    {/* Dummy SIRE — verificar si está habilitado */}
                    <Tooltip title="Verificar si sire-ws está habilitado (método dummy)">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          try {
                            Swal.fire({ title: "Verificando SIRE...", text: "Llamando al método dummy de sire-ws", allowOutsideClick: false, didOpen: () => Swal.showLoading() })
                            const resp = await postDummySire()
                            Swal.fire({ icon: "success", title: "SIRE accesible", html: `<p>El web service sire-ws respondió correctamente.</p><pre style="text-align:left;font-size:12px;max-height:200px;overflow:auto">${JSON.stringify(resp.data?.data, null, 2)}</pre>`, confirmButtonText: "Cerrar" })
                          } catch (err) {
                            const data = err.response?.data || {}
                            Swal.fire({ icon: "error", title: "SIRE no accesible", html: `<p>${data.message || err.message}</p>${data.hint ? `<p style="font-size:12px;color:#888">${data.hint}</p>` : ""}`, confirmButtonText: "Entendido" })
                          }
                        }}
                        sx={{ textTransform: "none", fontWeight: 500, borderRadius: 2, whiteSpace: "nowrap", borderColor: alpha("#27ae60", 0.6), color: "#27ae60" }}
                      >
                        Test SIRE
                      </Button>
                    </Tooltip>
                  </Box>

                  {/* Búsqueda, reset, más filtros */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={buttonFiltrado}
                      startIcon={<SearchIcon />}
                      sx={{
                        backgroundColor: "#5b5b5f",
                        textTransform: "none",
                        borderRadius: 2,
                        px: 2,
                        "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                      }}
                    >
                      Buscar
                    </Button>
                    <Tooltip title={filtersActive ? "Resetear filtros" : "Refrescar"}>
                      <IconButton
                        onClick={resetFilters}
                        color="primary"
                        size="small"
                        sx={{
                          bgcolor: filtersActive ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                          "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                        }}
                      >
                        {filtersActive ? <FilterAltOffIcon /> : <ReplayIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={mostrarMasFiltros ? "Ocultar filtros" : "Más filtros"}>
                      <IconButton
                        onClick={() => setMostrarMasFiltros(!mostrarMasFiltros)}
                        color="primary"
                        size="small"
                        sx={{
                          bgcolor: mostrarMasFiltros ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                        }}
                      >
                        {mostrarMasFiltros ? <CloseIcon /> : <MenuIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Collapse>

          {/* Panel de filtros adicionales */}
          <Collapse in={mostrarMasFiltros} timeout={300}>
            <Paper
              elevation={2}
              sx={{
                m: 1.5,
                mb: 1,
                borderRadius: 3,
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                  <MenuIcon fontSize="small" />
                  Filtros adicionales
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                  <TextField
                    label="Buscar por ID transacción"
                    type="number"
                    size="small"
                    value={idTransaccionSearch ?? ""}
                    onChange={(e) => setIdTransaccionSearch(e.target.value === "" ? null : Number(e.target.value))}
                    sx={{ minWidth: 170 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.5) }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Buscar CUIT proveedor"
                    size="small"
                    value={buscarCuitProveedor}
                    onChange={(e) => setBuscarCuitProveedor(e.target.value)}
                    sx={{ minWidth: 170 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ReceiptLongIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.5) }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Monto mín."
                    type="number"
                    size="small"
                    value={minVentas || ""}
                    onChange={(e) => setMinVentas(Number(e.target.value) || 0)}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    label="Monto máx."
                    type="number"
                    size="small"
                    value={maxVentas || ""}
                    onChange={(e) => setMaxVentas(Number(e.target.value) || 0)}
                    sx={{ width: 120 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Estado SIRE</InputLabel>
                    <Select
                      value={filtroEstado}
                      label="Estado SIRE"
                      onChange={(e) => setFiltroEstado(e.target.value)}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="pendiente">Solo pendientes</MenuItem>
                      <MenuItem value="informada">Solo informadas</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={mostrarDeletes}
                        onChange={(e) => setMostrarDeletes(e.target.checked)}
                        size="small"
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": { color: theme.palette.primary.main },
                        }}
                      />
                    }
                    label="Incluir eliminados"
                    sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                  />
                </Box>
              </Box>
            </Paper>
          </Collapse>

          {/* Título y subtítulo */}
          <Box sx={{ px: isMobile ? 1 : 1.5, pb: 0.5 }}>
            <Typography variant="h5" component="h1" fontWeight={700} sx={{ color: theme.palette.primary.main }}>
              Retenciones ARCA (SIRE)
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" color="text.secondary">
                Tipo de transacción {ID_TIPO_TRANSACCION_RETENCION_ARCA} — podés informar en SIRE las pendientes.
              </Typography>
              {filtroEstado !== "todos" && (
                <Chip
                  size="small"
                  label={filtroEstado === "pendiente" ? "Mostrando: Pendientes" : "Mostrando: Informadas"}
                  onDelete={() => setFiltroEstado("todos")}
                  sx={{
                    fontWeight: 600,
                    bgcolor: filtroEstado === "pendiente" ? alpha("#ed6c02", 0.12) : alpha("#2e7d32", 0.12),
                    color: filtroEstado === "pendiente" ? "#e65100" : "#1b5e20",
                  }}
                />
              )}
              {buscarCuitProveedor.trim() && (
                <Chip
                  size="small"
                  label={`CUIT: ${buscarCuitProveedor}`}
                  onDelete={() => setBuscarCuitProveedor("")}
                  sx={{ fontWeight: 600, bgcolor: alpha("#5b5b5f", 0.1) }}
                />
              )}
            </Box>
          </Box>

          {/* DataGrid */}
          <Paper
            elevation={1}
            sx={{
              flex: 1,
              m: 1.5,
              mt: 0.75,
              borderRadius: 3,
              backgroundColor: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <DataGrid
              key={isNavbarCollapsed ? "collapsed" : "expanded"}
              className="tablaVentasDataGrid"
              rows={filteredRows}
              columns={columns}
              loading={loading}
              getRowId={(r) => r.id}
              rowCount={totalCount}
              pagination
              paginationMode="server"
              sortingMode="server"
              sortingOrder={["desc", "asc"]}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              pageSizeOptions={[limit]}
              getRowHeight={() => 40}
              headerHeight={45}
              disableRowSelectionOnClick
              sx={{
                flex: 1,
                border: 0,
                "& .MuiDataGrid-footerContainer": { display: "none !important" },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#5b5b5f",
                  color: "white",
                  borderBottom: "2px solid #333",
                },
                "& .MuiDataGrid-columnHeaderTitle": { color: "white !important", fontWeight: 600 },
                "& .MuiDataGrid-menuIconButton": { color: "rgb(244, 177, 14) !important" },
                "& .MuiDataGrid-sortIcon": { color: "rgb(244, 177, 14) !important" },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.875rem",
                },
                "& .MuiDataGrid-row": {
                  "&:nth-of-type(even)": { backgroundColor: "#fafafa" },
                  "&:hover": { backgroundColor: alpha("#7b68ee", 0.06), transition: "background 0.2s ease" },
                },
              }}
              components={{ Pagination: () => null }}
            />
          </Paper>

          {/* Paginación */}
          <Box
            sx={{
              flexShrink: 0,
              p: 1.5,
              backgroundColor: "white",
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CustomPagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              montoTotalTransacciones={montoTotalTransacciones}
              vieneDe="tablaVentas"
              onPageChange={(newPage) => setPage(newPage)}
              montoTotalSeleccionado={0}
              cantidadSeleccionada={0}
            />
          </Box>
        </Box>

        <ModalNuevaRetencionArca
          open={openModal}
          onClose={() => setOpenModal(false)}
          onCreated={() => {
            fetchData(startDate, page, endDate, selectedUbicacion, minVentas, maxVentas, mostrarDeletes, idTransaccionSearch, dniCliente)
          }}
        />
      </ThemeProvider>
    </Box>
  )
}
